import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const PortalSearchSuggestions = ({
    query,
    searchTerm,
    setSearchTerm,
    suggestionsLength,
    zIndex,
    placeholder = "Search...",
    disabled = false,
    onSelect
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
    const inputRef = useRef(null);
    const timeoutRef = useRef(null);
    const containerRef = useRef(null);

    const updatePosition = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setPosition({
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                height: rect.height
            });
        }
    };

    const handleSearchChange = (e) => {
        if (disabled) return;

        const value = e.target.value;
        setSearchTerm(value);
        if (Array.isArray(query) && value.length > 0) {
            const filteredSuggestions = query.filter(item => {
                const searchProperty = item.username || item.name;
                return searchProperty && searchProperty.toLowerCase().includes(value.toLowerCase());
            });
            setSuggestions(filteredSuggestions);
            setSelectedSuggestionIndex(-1);
        } else {
            setSuggestions([]);
        }
    };

    const handleSuggestionSelect = (suggestion) => {
        if (disabled) return;

        const displayName = suggestion.username || suggestion.name;
        setSearchTerm(displayName);
        setSuggestions([]);
        setIsInputFocused(false);

        if (onSelect) {
            onSelect(suggestion);
        }
    };

    const handleKeyDown = (e) => {
        if (disabled) return;

        if (e.key === 'ArrowDown') {
            setSelectedSuggestionIndex(prevIndex => {
                const newIndex = Math.min(prevIndex + 1, suggestions.length - 1, suggestionsLength - 1);
                return newIndex;
            });
        } else if (e.key === 'ArrowUp') {
            setSelectedSuggestionIndex(prevIndex => Math.max(prevIndex - 1, 0));
        } else if (e.key === 'Enter' && selectedSuggestionIndex >= 0 && suggestions[selectedSuggestionIndex]) {
            handleSuggestionSelect(suggestions[selectedSuggestionIndex]);
        }
    };

    const handleFocus = () => {
        if (!disabled) {
            setIsInputFocused(true);
            updatePosition();
        }
    };

    const handleBlur = () => {
        if (!disabled) {
            // Delay hiding to allow for click on suggestion
            timeoutRef.current = setTimeout(() => {
                setIsInputFocused(false);
            }, 150);
        }
    };

    const handleSuggestionMouseDown = (suggestion) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        handleSuggestionSelect(suggestion);
    };

    useEffect(() => {
        const handleScroll = () => {
            if (isInputFocused && suggestions.length > 0) {
                updatePosition();
            }
        };

        const handleResize = () => {
            if (isInputFocused && suggestions.length > 0) {
                updatePosition();
            }
        };

        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isInputFocused, suggestions.length]);

    // Update position when suggestions change
    useEffect(() => {
        if (isInputFocused && suggestions.length > 0) {
            updatePosition();
        }
    }, [suggestions, isInputFocused]);

    const suggestionsList = suggestions.length > 0 && isInputFocused && !disabled ? createPortal(
        <div
            className='suggestions-list portal'
            style={{
                position: 'fixed',
                top: `${position.top + position.height}px`,
                left: `${position.left}px`,
                width: `${position.width}px`,
                zIndex: 9999,
                display: 'block',
            }}
        >
            {suggestions.slice(0, suggestionsLength).map((suggestion, index) => (
                <p
                    key={index}
                    onMouseDown={() => handleSuggestionMouseDown(suggestion)}
                    className={index === selectedSuggestionIndex ? 'selected-suggestion' : ''}
                >
                    {suggestion.username || suggestion.name}
                </p>
            ))}
        </div>,
        document.body
    ) : null;

    return (
        <div ref={containerRef} className='search-suggestions' style={{ zIndex: zIndex }}>
            <input
                ref={inputRef}
                className='market-searchbar'
                style={{ zIndex: zIndex + 1 }}
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                spellCheck="false"
                autoCorrect="off"
                disabled={disabled}
                readOnly={disabled}
            />
            {suggestionsList}
        </div>
    );
};

export default PortalSearchSuggestions;
